import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';
import { datadogLogs } from '@datadog/browser-logs';
import { useHistory } from 'react-router-dom';
import { INPUT_ERROR_TYPES, STORAGE_KEY } from 'shared/enums';
import { CURRENT_USER } from 'graphql/queries/user';
import { USER_ROUTES } from 'graphql/queries/routes';
import { LOGIN_USER } from 'graphql/mutations/user';
import { ToastBody, Input, Modal } from 'components';
import { getError, getDynamicError } from 'shared/helpers/auth';
import { flushErrorsState } from 'shared/helpers/flushErrorsState';
import CloseErrorIcon from 'assets/icons/error-close';
import { validateEmail } from 'validations';

import {
  LinkStyled,
  ButtonStyled,
  ConfirmationModalStyled
} from './user-credentials.styles';
import { phoneNumberAtom, twoFaMethodAtom } from '../state';

export default ({ handleNext, setToken, t }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [redirectModalOpen, setRedirectModalOpen] = useState(false);
  const [login] = useMutation(LOGIN_USER);
  const token = useRef('');
  const setPhoneNumberState = useSetRecoilState(phoneNumberAtom);
  const setTwoFaMethodAtom = useSetRecoilState(twoFaMethodAtom);
  const history = useHistory();

  const handleSubmit = async () => {
    const emailValue = email?.trim?.();
    const errorsList = validateEmail({ email, t });
    if (errorsList.length > 0) {
      datadogLogs.logger.warn('Insights authentication error', {}, errorsList);
      return setErrors(errorsList);
    }
    const loginDateToken = localStorage.getItem(STORAGE_KEY.LOGIN_TOKEN);
    await login({
      variables: {
        email: emailValue,
        password,
        loginDateToken
      },
      refetchQueries: [{ query: CURRENT_USER }, { query: USER_ROUTES }],
      onCompleted: ({
        login: {
          verified,
          token: resultToken,
          loggedIn,
          countryCode,
          phoneNumber,
          method
        }
      }) => {
        if (!verified) {
          token.current = resultToken;
          setRedirectModalOpen(true);
        }
        if (loggedIn) {
          // remove passthrough if set from token login
          localStorage.removeItem('passthrough');

          // when a user is redirected to /signin user would get stuck see user-2facode.js for more info
          history.push({
            pathname: '/',
            state: {
              isLogged: true
            }
          });
        }
        if (resultToken && !loggedIn) {
          const phoneNumberState = `+${countryCode} ${phoneNumber}`;
          setToken(resultToken);
          setPhoneNumberState(phoneNumberState);
          setTwoFaMethodAtom(method);
          handleNext();
        }
      },
      onError: error => {
        const validationErrors =
          error.graphQLErrors?.[0]?.extensions?.exception?.payload;
        if (Array.isArray(validationErrors)) {
          setErrors(validationErrors);
        } else if (error.graphQLErrors?.[0]?.message) {
          toast.error(
            <ToastBody
              category="error"
              title={error.graphQLErrors?.[0]?.message}
              text={error.graphQLErrors?.[0]?.extensions?.payload}
            />
          );
        }
        datadogLogs.logger.warn('Insights authentication error', {}, error);
      }
    });
  };

  const emailError = getError({ name: 'email', errors, t });
  const passwordError = getError({ name: 'password', errors, t });
  const lockoutError = getDynamicError({
    type: INPUT_ERROR_TYPES.LOCKOUT,
    errors,
    t
  });

  return (
    <>
      {redirectModalOpen && (
        <Modal
          data-pendoid="signin_modal_error_title"
          title={t?.('signin_modal_error_title')}
          size="small"
          buttons={[
            {
              text: t?.('buttons_cancel'),
              label: t?.('buttons_cancel'),
              category: 'outline',
              size: 'large',
              handleClick: () => setRedirectModalOpen(false)
            },
            {
              text: t?.('signin_modal_error_button'),
              label: t?.('signin_modal_error_button'),
              size: 'large',
              link: `/auth/${token.current}`
            }
          ]}
          closeModal={() => setRedirectModalOpen(false)}
        >
          <ConfirmationModalStyled data-pendoid="signin_modal_error_message">
            <p>{t?.('signin_modal_error_message')}</p>
            <p>{t?.('signin_modal_error_message2')}</p>
          </ConfirmationModalStyled>
        </Modal>
      )}
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <Input
          data-pendoid="input_email"
          label={t?.('input_email')}
          name="email"
          type="text"
          value={email}
          handleChange={e =>
            flushErrorsState({
              errors,
              name: 'email',
              value: e.target.value,
              updateErrors: setErrors,
              updateState: setEmail
            })
          }
          error={Boolean(emailError)}
          errorMessage={errors.length ? emailError : null}
          errorPlaceholder={true}
          showIcon={Boolean(emailError)}
          icon={CloseErrorIcon}
        />
        <Input
          data-pendoid="input_password"
          label={t?.('input_password')}
          name="password"
          type="password"
          value={password}
          handleChange={e =>
            flushErrorsState({
              errors,
              name: 'password',
              value: e.target.value,
              updateErrors: setErrors,
              updateState: setPassword
            })
          }
          error={Boolean(passwordError || lockoutError)}
          errorMessage={errors.length ? passwordError || lockoutError : null}
          errorPlaceholder={true}
          showIcon={Boolean(passwordError)}
          icon={CloseErrorIcon}
        />
        <ButtonStyled
          data-pendoid="signin_button"
          type="submit"
          label={t?.('signin_button')}
        >
          {t?.('signin_button')}
        </ButtonStyled>
        <LinkStyled ariaLabel="signin_reset_password" to="/reset">
          {t?.('signin_reset_password')}
        </LinkStyled>
      </form>
    </>
  );
};
